import { PiPlus, PiTruck } from 'react-icons/pi'

import { StatusBadge } from '../FbUI/StatusBadge'
import { cn } from '../UI/cn'
import type { SampleRequest } from '../../models/samples'

interface SampleTrackingCellProps {
  sample: SampleRequest
  /** Standard/admin on a fulfillable sample — enables the add/edit affordance. */
  canEdit: boolean
  onEdit: (sample: SampleRequest) => void
}

/**
 * Tracking-number table cell (FIR-7033).
 *
 * A sample WITH a tracking number reads as data — a neutral truck badge that
 * can be clicked to edit. A sample WITHOUT one reads as an action — a muted
 * "Add tracking" button. The two look deliberately different so you can tell at
 * a glance which samples have shipped, instead of both being identical green
 * links. Read-only viewers see the value or a dash, never an affordance.
 */
export function SampleTrackingCell({
  sample,
  canEdit,
  onEdit,
}: SampleTrackingCellProps) {
  const value = sample.tracking_number ?? ''
  const isCancelled = sample.status === 'cancelled'

  // Read-only: plain value (or dash), dimmed if cancelled.
  if (!canEdit) {
    if (!value) return <span className="text-fg-tertiary">-</span>
    return (
      <StatusBadge
        color="neutral"
        icon={PiTruck}
        className={cn('max-w-full', isCancelled && 'opacity-60')}
      >
        <span className="truncate">{value}</span>
      </StatusBadge>
    )
  }

  // Editable + has tracking: a clickable value badge.
  if (value) {
    return (
      <button
        type="button"
        onClick={() => onEdit(sample)}
        aria-label={`Edit tracking ${value}`}
        className="max-w-full rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-active/40"
      >
        <StatusBadge
          color="neutral"
          icon={PiTruck}
          className="max-w-full transition-colors hover:bg-neutral-200"
        >
          <span className="truncate">{value}</span>
        </StatusBadge>
      </button>
    )
  }

  // Editable + no tracking: a muted "add" action, visually distinct from a value.
  return (
    <button
      type="button"
      onClick={() => onEdit(sample)}
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-fg-tertiary transition-colors hover:bg-surface-sunken hover:text-fg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-active/40"
    >
      <PiPlus className="size-3.5 shrink-0" />
      Add tracking
    </button>
  )
}
